import { SortableContext } from "@dnd-kit/sortable";
import type {
  Header,
  Table as ReactTable,
  SortDirection,
} from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type { MouseEvent } from "react";
import { useMemo } from "react";
import type { FilterValue } from "@/components/filter-popover";
import { TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";
import { DraggableTableHead } from "./draggable-table-head";

interface DraggableTableHeaderProps<TData> {
  table: ReactTable<TData>;
  columnOrder: string[];
  handleSort: (colId: string) => void;
  sortConfig: { key: string; direction: SortDirection | null };
  renderSortIcon: (column: UnifiedTableColumn<TData>) => React.ReactNode;
  filters: Record<string | keyof TData, unknown>;
  updateFilter: (colId: string, filter: FilterValue) => void;
  clearFilter: (colId: string) => void;
  columns: UnifiedTableColumn<TData>[];
  // Optional props forwarded to DraggableTableHead
  handleDoubleClickResize?: (e: MouseEvent, colId: string) => void;
  isMetricLimitExceeded?: boolean;
  getMetricLimitTooltipText?: () => string;
  // Pin functionality
  pinnedColumns?: string[];
  onTogglePin?: (columnId: string) => void;
  // Resize functionality
  onResizeToFit?: (columnId: string) => void;
  // Remove column functionality
  onRemoveColumn?: (columnId: string) => void;
  // Custom class name for the table header
  headerClassName?: string;
  // Alias functionality
  columnAliases?: Record<string, string>;
  onSetAlias?: (columnId: string) => void;
  // Wrap functionality
  wrappedColumns?: string[];
  onToggleWrap?: (columnId: string) => void;
  // Read-only state
  isLocked?: boolean;
}

// Constant empty array to avoid creating new reference on each render
const EMPTY_PINNED_COLUMNS: string[] = [];

export function DraggableTableHeader<TData>({
  table,
  columnOrder,
  handleSort,
  sortConfig,
  renderSortIcon,
  filters,
  updateFilter,
  clearFilter,
  columns,
  handleDoubleClickResize,
  isMetricLimitExceeded,
  getMetricLimitTooltipText,
  pinnedColumns = EMPTY_PINNED_COLUMNS,
  onTogglePin,
  onResizeToFit,
  onRemoveColumn,
  columnAliases,
  onSetAlias,
  wrappedColumns = [],
  onToggleWrap,
  headerClassName,
  isLocked = false,
}: DraggableTableHeaderProps<TData>) {
  const headerGroups = table.getHeaderGroups();

  // Disable drag-reordering while a column is being resized.
  const disableSortable = Boolean(
    table.getState().columnSizingInfo?.isResizingColumn,
  );

  // Extract columnSizing state to avoid complex expression in dependency array

  // Memoize pinnedColumnPositions calculation to avoid recalculating on every render
  // Include columnSizing in dependencies to recalculate when column widths change
  // biome-ignore lint/correctness/useExhaustiveDependencies: columnSizing needed to recalculate positions when columns are resized
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table, table.getState().columnSizing]);

  // Separate pinned and unpinned columns for restricted dragging
  const { pinnedHeaders, unpinnedHeaders } = useMemo(() => {
    const allHeaders = headerGroups[0]?.headers || [];
    const pinned: typeof allHeaders = [];
    const unpinned: typeof allHeaders = [];

    // Sort headers according to column order and separate by pinned status
    const sortedHeaders = [...allHeaders].sort((a, b) => {
      const indexA = columnOrder.indexOf(a.id);
      const indexB = columnOrder.indexOf(b.id);
      return indexA - indexB;
    });

    sortedHeaders.forEach((header) => {
      if (pinnedColumns.includes(header.id)) {
        pinned.push(header);
      } else {
        unpinned.push(header);
      }
    });

    return { pinnedHeaders: pinned, unpinnedHeaders: unpinned };
  }, [headerGroups, columnOrder, pinnedColumns]);

  // Get sortable items for each section
  const pinnedItems = useMemo(
    () => pinnedHeaders.map((h) => h.id),
    [pinnedHeaders],
  );
  const unpinnedItems = useMemo(
    () => unpinnedHeaders.map((h) => h.id),
    [unpinnedHeaders],
  );

  const renderHeader = (header: Header<TData, unknown>) => {
    const isPinned = pinnedColumns.includes(header.id);
    const pinnedLeft = pinnedColumnPositions[header.id];

    return (
      <DraggableTableHead<TData, unknown>
        key={header.id}
        header={header}
        table={table}
        handleSort={handleSort}
        sortConfig={sortConfig}
        renderSortIcon={renderSortIcon}
        filters={filters}
        updateFilter={updateFilter}
        clearFilter={clearFilter}
        columns={columns}
        handleDoubleClickResize={handleDoubleClickResize}
        isMetricLimitExceeded={isMetricLimitExceeded}
        getMetricLimitTooltipText={getMetricLimitTooltipText}
        columnTitle={flexRender(
          header.column.columnDef.header,
          header.getContext(),
        )}
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        onResizeToFit={onResizeToFit}
        onRemoveColumn={onRemoveColumn}
        columnAliases={columnAliases}
        onSetAlias={onSetAlias}
        isWrapped={wrappedColumns.includes(header.column.id)}
        onToggleWrap={onToggleWrap}
        pinnedLeft={pinnedLeft}
        isLocked={isLocked}
      />
    );
  };

  return (
    <TableHeader className={headerClassName}>
      {headerGroups.map((headerGroup) => (
        <TableRow
          key={headerGroup.id}
          className={cn(
            "bg-muted/50",
            pinnedHeaders.length > 0 && "border-b border-r border-border",
          )}
        >
          {/* Pinned columns section - restricted dragging within pinned area only */}
          {pinnedHeaders.length > 0 && (
            <SortableContext disabled={disableSortable} items={pinnedItems}>
              {pinnedHeaders.map(renderHeader)}
            </SortableContext>
          )}

          {/* Unpinned columns section - restricted dragging within unpinned area only */}
          {unpinnedHeaders.length > 0 && (
            <SortableContext disabled={disableSortable} items={unpinnedItems}>
              {unpinnedHeaders.map(renderHeader)}
            </SortableContext>
          )}
        </TableRow>
      ))}
    </TableHeader>
  );
}
